<!-- Production Desktop Notification Container Component -->
<!-- Displays desktop notifications and permission prompts for the Scan Ninja application -->

<!-- Desktop Notification Permission Prompt -->
@if (showPermissionPrompt) {
  <div
    class="notification-permission-prompt animate__animated animate__slideInRight"
  >
    <div class="permission-header">
      <i class="fas fa-desktop"></i>
      <strong>Enable Desktop Notifications</strong>
      <button
        type="button"
        class="btn-close"
        (click)="dismissPermissionPrompt()"
        aria-label="Dismiss permission prompt"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="permission-body">
      <p>
        Stay informed about critical security alerts even when Scan Ninja is not
        in focus.
      </p>
      <div class="permission-actions">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          (click)="requestNotificationPermission()"
          [disabled]="isRequestingPermission"
        >
          @if (isRequestingPermission) {
            <i class="fas fa-spinner fa-spin me-1"></i>
            Requesting...
          } @else {
            <i class="fas fa-check me-1"></i>
            Enable Notifications
          }
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          (click)="dismissPermissionPrompt()"
          [disabled]="isRequestingPermission"
        >
          Not Now
        </button>
      </div>
    </div>
  </div>
}

<!-- In-App Toast Notifications (Fallback) -->
<div
  class="toast-container"
  role="region"
  aria-label="Notifications"
  aria-live="polite"
>
  @for (
    notification of notifications;
    track trackByNotificationId($index, notification)
  ) {
    <div
      [class]="getNotificationClasses(notification.type)"
      [attr.data-notification-id]="notification.id"
      role="alert"
      [attr.aria-labelledby]="'toast-title-' + notification.id"
      [attr.aria-describedby]="'toast-message-' + notification.id"
    >
      <!-- Notification Header -->
      <div class="toast-header">
        <i
          [class]="getNotificationIcon(notification.type)"
          [class.text-success]="notification.type === 'success'"
          [class.text-danger]="notification.type === 'error'"
          [class.text-warning]="notification.type === 'warning'"
          [class.text-info]="notification.type === 'info'"
          [attr.aria-label]="notification.type + ' notification'"
        ></i>
        <strong class="toast-title" [id]="'toast-title-' + notification.id">{{
          getSanitizedText(notification.title)
        }}</strong>
        <small class="toast-time">{{
          formatTime(notification.timestamp)
        }}</small>
        <button
          type="button"
          class="btn-close"
          (click)="removeNotification(notification.id)"
          (keyup.enter)="removeNotification(notification.id)"
          [attr.aria-label]="
            'Close ' + getSanitizedText(notification.title) + ' notification'
          "
          [title]="
            'Close ' + getSanitizedText(notification.title) + ' notification'
          "
          tabindex="0"
        >
          <span class="visually-hidden"
            >Close {{ getSanitizedText(notification.title) }} notification</span
          >
          <i class="fas fa-times text-info" aria-hidden="true"></i>
        </button>
      </div>
      <!-- Notification Body -->
      <div class="toast-body">
        <p
          class="toast-message"
          [id]="'toast-message-' + notification.id"
          [innerHTML]="getSanitizedMessage(notification.message)"
        ></p>
        <!-- Action Button (if provided) -->
        @if (notification.action) {
          <div class="toast-actions">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              (click)="executeAction(notification)"
              (keyup.enter)="executeAction(notification)"
              [attr.aria-label]="
                getSanitizedText(notification.action.label) +
                ' for ' +
                getSanitizedText(notification.title)
              "
              tabindex="0"
            >
              {{ getSanitizedText(notification.action.label) }}
            </button>
          </div>
        }
      </div>
      <!-- Progress Bar for timed notifications -->
      @if (notification.duration && notification.duration > 0) {
        <div
          class="toast-progress"
          [style.animation-duration.ms]="notification.duration"
          role="progressbar"
          aria-label="Notification auto-close timer"
          title="Notification auto-close timer"
          [attr.aria-labelledby]="'toast-progress-label-' + notification.id"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="100"
        >
          <span
            [id]="'toast-progress-label-' + notification.id"
            class="visually-hidden"
          >
            Notification will auto-close in
            {{ notification.duration / 1000 }} seconds
          </span>
        </div>
      }
    </div>
  }
</div>
